/**
 * Web-Vitals -> Sentry Metrics-Pipeline.
 *
 * Wird einmalig aus `main.tsx` aufgerufen, nachdem `initSentry()` gelaufen
 * ist. Die Web-Vitals-Bibliothek registriert PerformanceObserver, die
 * sich beim ersten Vitals-Wert (oder einer aenderung) bei uns melden.
 *
 * Wir leiten die Werte als `Sentry.metrics.distribution` an Sentry weiter
 * — dort lassen sie sich aggregieren (P50/P75/P95) und mit Releases
 * korrelieren.
 *
 * Was hier *nicht* passiert:
 *   - kein DSGVO-relevanter Datenfluss: nur Performance-Zahlen, keine
 *     Patientendaten.
 *   - keine zusaetzlichen Network-Calls: `web-vitals` haengt nur an den
 *     bestehenden PerformanceObserver-APIs des Browsers.
 *
 * Bundlegroesse: `web-vitals` ist ~3kb gz, vernachlaessigbar.
 *
 * SDK-Hinweis: Das Sentry-JS-v10-SDK exportiert eine
 * `Sentry.metrics.distribution(name, value, options)`-API, deren Optionen
 * `unit` und `attributes` enthalten. (`tags` heisst hier `attributes`.)
 */

import * as Sentry from '@sentry/react';
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';

import { env } from './env';

/** Welche Einheit Sentry erwartet, abhaengig vom Metrik-Namen. */
function unitFor(metricName: Metric['name']): string {
  // CLS ist ein dimensionsloser Score (0..n), alle anderen sind Zeiten.
  return metricName === 'CLS' ? 'ratio' : 'millisecond';
}

/**
 * Schickt einen einzelnen Web-Vital als Sentry-Distribution-Metric.
 * Exportiert (statt nur inline), damit unit-getestet werden kann ohne den
 * vollen `reportWebVitals()`-Initialisierungspfad zu durchlaufen.
 */
export function sendWebVital(metric: Metric): void {
  if (!env.sentryDsn) return;

  Sentry.metrics.distribution(
    `web-vitals.${metric.name.toLowerCase()}`,
    metric.value,
    {
      unit: unitFor(metric.name),
      attributes: {
        metric_id: metric.id,
        metric_rating: metric.rating,
        navigation_type: metric.navigationType,
      },
    },
  );
}

/**
 * Registriert die PerformanceObserver fuer alle relevanten Web-Vitals.
 * Idempotent: ein erneuter Aufruf wuerde dieselben Observer doppelt
 * registrieren – daher genau einmal aus `main.tsx` aufrufen.
 */
export function reportWebVitals(): void {
  // Wenn keine Sentry-DSN konfiguriert ist (z.B. lokales Dev ohne Sentry),
  // sparen wir uns die PerformanceObserver komplett. Die Werte landeten
  // sonst nur in `sendWebVital` und wuerden dort verworfen, aber der
  // Browser-Overhead waere unnoetig.
  if (!env.sentryDsn) return;

  onCLS(sendWebVital);
  onINP(sendWebVital);
  onLCP(sendWebVital);
  onTTFB(sendWebVital);
  onFCP(sendWebVital);
}
